import { apiClient } from './client';
import { Child, ChildCreate, ChildUpdate, PaginatedResponse, ChildFilters } from '../../types/api';

// The API returns first_name/last_name/date_of_birth but the frontend uses name/surname/birthdate.
// Map at the service boundary so all downstream code works unchanged.
// Tolerates both formats (real API data and MSW mock data).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapChild(raw: any): Child {
  const child = {
    ...raw,
    name: raw.first_name || raw.name || '',
    surname: raw.last_name || raw.surname || '',
    birthdate: raw.date_of_birth || raw.birthdate || '',
  };
  delete child.first_name;
  delete child.last_name;
  delete child.date_of_birth;
  return child as Child;
}

function mapChildCreate(data: ChildCreate) {
  const { name, surname, birthdate, ...rest } = data;
  return { ...rest, first_name: name, last_name: surname, date_of_birth: birthdate };
}

function mapChildUpdate(data: ChildUpdate) {
  const { name, surname, birthdate, ...rest } = data;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapped: Record<string, any> = { ...rest };
  if (name !== undefined) mapped.first_name = name;
  if (surname !== undefined) mapped.last_name = surname;
  if (birthdate !== undefined) mapped.date_of_birth = birthdate;
  return mapped;
}

export const childrenApi = {
  getChildren: async (filters?: ChildFilters): Promise<PaginatedResponse<Child>> => {
    const response = await apiClient.get<any>('/children/', {
      params: filters,
    });
    const data = response.data;
    return { ...data, items: (data.items || data || []).map(mapChild) };
  },

  createChild: async (data: ChildCreate): Promise<Child> => {
    const response = await apiClient.post<any>('/children/', mapChildCreate(data));
    return mapChild(response.data);
  },

  getChild: async (childId: string): Promise<Child> => {
    const response = await apiClient.get<any>(`/children/${childId}`);
    return mapChild(response.data);
  },

  updateChild: async (childId: string, data: ChildUpdate): Promise<Child> => {
    const response = await apiClient.put<any>(`/children/${childId}`, mapChildUpdate(data));
    return mapChild(response.data);
  },

  deleteChild: async (childId: string): Promise<void> => {
    await apiClient.delete(`/children/${childId}`);
  },

  getChildrenByClassroom: async (classroomId: string): Promise<Child[]> => {
    const response = await apiClient.get<any[]>(`/classrooms/${classroomId}/children`);
    return response.data.map(mapChild);
  },

  getChildrenByGuardian: async (guardianId: string): Promise<Child[]> => {
    const response = await apiClient.get<any[]>(`/guardians/${guardianId}/children`);
    return response.data.map(mapChild);
  },
};
